import {
  type SkinLayerPlan,
  type SkinSpritePlacement,
  type SkinVisualPlan,
  skinHullDiameter,
  skinHullOffsetToWorld,
  skinSpritePlacement,
  skinTexturePointToWorld,
} from '@kenocyber/shared';
import Phaser from 'phaser';
import { DEV_TOOLS_ALLOWED } from '../dev-tools';
import { ensureStreakTexture } from './projectile-art';
import { ensureSkinParticleTexture, ensureSkinSealTexture, skinColorValue } from './skin-art';
import { trailFrameIsDiscontinuous } from './skin-trail-continuity';
import { type TrailFadeState, trailFade } from './skin-trail-fade';
import { trailParticleOutOfRange } from './skin-trail-range';

/**
 * Draws one skin: its hull art and its effect layers (ADR 0041).
 *
 * ## Generic by construction
 *
 * **There is no skin id in this file.** It consumes a compiled
 * {@link SkinVisualPlan} - a list of layers with a texture, a ceiling, a depth
 * and an offset - and it cannot tell which skin it is drawing. That is the
 * whole design: a renderer that knew would grow one branch per skin for ever,
 * and every branch would be a place a gameplay rule could be smuggled in.
 *
 * ## Presentation only
 *
 * Nothing here returns a position, a velocity, a rotation or a scale to
 * anybody. Every frame it is TOLD where the ship is and how big it is drawn,
 * and it puts pixels there. It cannot move a ship, change a hitbox, alter
 * damage or touch the economy, because it has no way to say any of those things.
 *
 * ## One canvas, one context, and everything owned
 *
 * It creates only Phaser display objects on the scene it was given. No second
 * canvas, no second renderer, no shader, no post-processing. Every sprite,
 * emitter and timer it makes is held in a field and released by
 * {@link SkinRenderer.destroy}, which the host calls on close, on a skin change
 * and on scene shutdown.
 *
 * Nothing is allocated per frame: the emitters are built once, the seal sprites
 * are built once, and `update` only writes numbers onto objects that already
 * exist.
 */

/**
 * DEV/E2E ONLY - what one trail particle was born with.
 *
 * ## Why it is stamped on the particle instead of buffered
 *
 * The question this answers is per PARTICLE: which speed fixed its lifespan,
 * and how far had the ship gone by the time it died. A ring buffer would have
 * to be searched, bounded, and kept in step with a pool that reuses objects.
 * Riding on the particle, the record lives exactly as long as the thing it
 * describes, is bounded by `maxParticles` by construction, and can never be
 * matched to the wrong owner: a probe reads it from the very emitter whose
 * `alive` list holds it.
 *
 * ## And why the birth is captured by Phaser's own callback
 *
 * `ParticleEmitter.onParticleEmit` is invoked with the particle immediately
 * after `fire()` succeeds. Nothing here assumes "the last one in the list" -
 * and it must not: this version does `alive.unshift(particle)` unless
 * `particleBringToTop` is set, so the newest particle is at index 0, not at the
 * end.
 *
 * Absent from production: every write is behind `DEV_TOOLS_ALLOWED`, compared
 * directly so the branch is dead code the bundler removes.
 */
export interface TrailParticleBirth {
  /** Monotonic per renderer, so a sequence can be followed across frames. */
  readonly seq: number;
  /** The scene clock when it was laid down. */
  readonly atMs: number;
  /** Where the hull was, and where the particle actually started. */
  readonly hullX: number;
  readonly hullY: number;
  readonly bornX: number;
  readonly bornY: number;
  /** The hull diameter in world units this frame, including the visual scale. */
  readonly diameter: number;
  /** The contract, in hull diameters. */
  readonly maxLengthHulls: number | null;
  /** The DRAWN speed of that frame, in world units per second. */
  readonly speed: number;
  /** The gate the frame had to clear to emit at all. */
  readonly minSpeed: number;
  /** `maxLength * diameter / speed`, in ms. Infinity when there is no contract. */
  readonly capMs: number;
  /** What the descriptor rolled, and what the emitter was actually given. */
  readonly authoredMs: number;
  readonly appliedMs: number;
  /** Direction of travel at birth, in radians. */
  readonly travelRad: number;
}

/** What the host tells the renderer, every frame. */
export interface SkinHostState {
  /** The AUTHORITATIVE position of the ship. Never modified. */
  readonly x: number;
  readonly y: number;
  /** The sprite rotation the host already computed (`facing + PI/2`). */
  readonly rotation: number;
  /** One hull diameter in world units, before the visual scale. */
  readonly hullWorldUnits: number;
  /** The scale the server's maximum health already derived. */
  readonly visualScale: number;
  /** How fast the ship is really moving on screen, in world units per second. */
  readonly speed: number;
}

interface LayerRuntime {
  readonly plan: SkinLayerPlan;
  readonly emitter: Phaser.GameObjects.Particles.ParticleEmitter | null;
  readonly seals: Phaser.GameObjects.Image[];
  /**
   * The longest side of this layer's texture, in source pixels.
   *
   * The descriptor states a particle's size as a fraction of the hull's drawn
   * diameter, and that has to mean the same thing whatever shape it is drawn
   * with. Dividing by a constant 16 made it mean "a fraction of a hull" for the
   * two square textures and something 25% longer for `star-streak`, which is
   * 20x6 - so an author moving a trail from one shape to another silently got a
   * different size. The longest side is the one that decides how long the
   * particle reads, so it is the one the fraction is measured against.
   */
  readonly textureLongestSide: number;
  /** Milliseconds owed to the next emission, so a rate is a rate. */
  accumulatorMs: number;
  /** Which palette entry particles born right now take. */
  colorIndex: number;
  /** How long the current palette entry has been held, in milliseconds. */
  paletteHoldMs: number;
  rotation: number;
}

export interface SkinRendererOptions {
  readonly scene: Phaser.Scene;
  readonly plan: SkinVisualPlan;
  /** The hull image the skin replaces the texture of. */
  readonly hull: Phaser.GameObjects.Image;
  /** The loaded texture key for this skin's PNG. */
  readonly textureKey: string;
}

export class SkinRenderer {
  private readonly scene: Phaser.Scene;
  private readonly plan: SkinVisualPlan;
  private readonly hull: Phaser.GameObjects.Image;
  private readonly textureKey: string;
  private readonly layers: LayerRuntime[] = [];
  private destroyed = false;
  /** Where the ship was drawn last frame, for the trail's own direction. */
  private lastDrawn: { x: number; y: number } | null = null;
  /**
   * Whether residue was laid down on the last frame, for the census.
   *
   * Diagnostic only, and read by nothing that draws. It exists because "is
   * residue being laid down at all" is a fact about a DECISION, and a
   * screenshot can only ever be evidence about a pixel. Which way the ship is
   * travelling belongs to the scene, which measures it with or without a skin.
   */
  private trailEmitted = false;
  /**
   * DEV/E2E ONLY. The record the next emitted particle will carry, and the
   * counter that numbers it. Written only inside `DEV_TOOLS_ALLOWED`, so the
   * production bundle has neither.
   */
  private pendingBirth: TrailParticleBirth | null = null;
  private birthSeq = 0;
  /**
   * The placement the HULL was drawn with this frame, and the texture it was
   * drawn from.
   *
   * Held so the body sparkle can be positioned by the very same transform
   * rather than by a second copy of it. A second copy is a second opinion, and
   * the first frame the two disagreed the sparkle would drift off the ship.
   */
  private placement: SkinSpritePlacement | null = null;
  private textureSize: { width: number; height: number } = { width: 1, height: 1 };
  /**
   * Which mask point the next body particle takes.
   *
   * A cursor rather than a random index: the points are already spread over the
   * silhouette by the pipeline's lattice, so walking them in order covers the
   * hull evenly instead of clumping the way sampling with replacement does.
   */
  private maskSeq = 0;

  constructor(options: SkinRendererOptions) {
    this.scene = options.scene;
    this.plan = options.plan;
    this.hull = options.hull;
    this.textureKey = options.textureKey;

    /*
     * The hull texture is swapped and its ORIGIN is taken from the metadata.
     *
     * `clearTint()` rather than a tint of any kind: a skin is the whole image
     * of a ship, and multiplying a colour through it would show a ship nobody
     * drew. The same reason `PvpScene` already refuses to tint a hull.
     */
    this.hull.setTexture(this.textureKey);
    this.hull.clearTint();
    this.hull.setOrigin(this.plan.pivot.x, this.plan.pivot.y);

    const streak = ensureStreakTexture(this.scene as never);
    for (const layer of this.plan.layers) {
      this.layers.push(this.buildLayer(layer, streak));
    }
  }

  /** One layer's runtime: an emitter, or a set of seal sprites. */
  private buildLayer(plan: SkinLayerPlan, streak: string): LayerRuntime {
    if (plan.circle) {
      /*
       * A seal is DRAWN, not emitted: it has no cadence and no lifespan, so it
       * is a set of images that follow the hull. One texture serves every skin,
       * tinted from the palette.
       */
      /*
       * The painted ring, when it is on the GPU; the procedural seal otherwise.
       *
       * `vfx-ring` is a crisp white stroke with a hollow centre and a soft
       * halo, drawn by a person - the procedural seal is arcs from a few
       * `fillCircle` calls, and the player's verdict on it was that it did not
       * read at all. The fallback is not a compromise on quality: it is what a
       * frame shows if the PNG has not landed yet, and it lasts one load.
       */
      const texture = this.scene.textures.exists('vfx-ring')
        ? 'vfx-ring'
        : ensureSkinSealTexture(this.scene as never);
      const seals: Phaser.GameObjects.Image[] = [];
      for (let index = 0; index < plan.circle.count; index += 1) {
        const image = this.scene.add.image(0, 0, texture);
        image.setDepth(plan.depth);
        image.setBlendMode(Phaser.BlendModes.ADD);
        image.setTint(skinColorValue(plan.colors[index % plan.colors.length] as string));
        image.setAlpha(plan.alpha.max);
        seals.push(image);
      }
      return {
        plan,
        emitter: null,
        seals,
        textureLongestSide: 16,
        accumulatorMs: 0,
        colorIndex: 0,
        paletteHoldMs: 0,
        rotation: 0,
      };
    }

    if (plan.frequencyHz <= 0) {
      // A static signature with no cadence and no circle: nothing to run.
      return {
        plan,
        emitter: null,
        seals: [],
        textureLongestSide: 16,
        accumulatorMs: 0,
        colorIndex: 0,
        paletteHoldMs: 0,
        rotation: 0,
      };
    }

    const texture = plan.texture
      ? ensureSkinParticleTexture(this.scene as never, plan.texture, streak)
      : streak;
    const emitter = this.scene.add.particles(0, 0, texture, {
      /*
       * A plain number rather than the declared range, because the lifespan is
       * chosen per emission: a trail's residue is capped by the length the
       * descriptor allows, and that cap depends on how fast the ship is going
       * right now. `setParticleLifespan` only honours a scalar.
       */
      lifespan: plan.lifespanMs.max,
      alpha: { start: plan.alpha.max, end: plan.alpha.min },
      blendMode: plan.blend === 'add' ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL,
      /*
       * The hard ceiling comes from the PLAN, which the descriptor's own limits
       * already bounded and which no profile or level of detail can raise.
       */
      maxAliveParticles: plan.maxParticles,
      emitting: false,
    });
    emitter.setDepth(plan.depth);
    /*
     * DEV/E2E ONLY: stamp every trail particle with what it was born with.
     *
     * Phaser's own emit callback, which receives the particle it just fired -
     * see `TrailParticleBirth` for why nothing here may guess which one it is.
     */
    if (DEV_TOOLS_ALLOWED && emitter && plan.role === 'trail') {
      emitter.onParticleEmit((particle: unknown) => {
        (particle as { __kenoTrail?: TrailParticleBirth | null }).__kenoTrail = this.pendingBirth;
      });
    }
    return {
      plan,
      emitter,
      seals: [],
      textureLongestSide: this.longestSideOf(texture),
      accumulatorMs: 0,
      colorIndex: 0,
      paletteHoldMs: 0,
      rotation: 0,
    };
  }

  /**
   * Emits one particle AT a world point, and makes sure it is there.
   *
   * ## Why the position has to be applied again
   *
   * This renderer emits with absolute world coordinates into an emitter that
   * sits at the origin. Phaser 4.2.1 fires a particle with
   * `Particle.fire(x, y)`, which calls `getEmitZone` - a no-op when no zone is
   * declared - and then does `this.x += x`.
   *
   * A particle straight out of `new` starts at (0, 0) and the sum lands on the
   * point asked for. A particle taken back off the `dead` pool still holds the
   * coordinates it died at, and the sum lands on **old + new**. Measured in a
   * team match, where the ships travel along x and hold y, so a recycled
   * particle's y had to double if the sum was real:
   *
   * ```
   * asked for  733,1099   read at   992,2194   ratio y 2.00
   * asked for 1400,1498   read at  5174,4499   ratio y 3.00  <- third life
   * ```
   *
   * It is also the particle an earlier gate recorded at world (20461, 8245)
   * with a correct birth record and could not explain.
   *
   * ## Why here and not by moving the emitter
   *
   * Placing the emitter on the ship and emitting relative coordinates would fix
   * the sum and break something worse: residue is static in the WORLD, and every
   * particle's position is stored in the emitter's space. Moving the emitter
   * would drag the whole wake along with the hull, which is the one thing a
   * trail must never do.
   *
   * So the particle is fired and then pinned. `emitParticleAt` returns the
   * particle it fired - or nothing, when the emitter is at its limit - and
   * Phaser's own `onParticleEmit` has already run by then, so the DEV birth
   * record exists before this and is re-stamped after, with the position that
   * ended up being true.
   */
  private emitAt(
    emitter: Phaser.GameObjects.Particles.ParticleEmitter,
    x: number,
    y: number,
    /**
     * Where the hull is and how long the band is, for a trail.
     *
     * Residue is born BEHIND the ship - `offset.forward` is negative - so a
     * particle is already a fraction of the way across its band on the frame it
     * appears. Without this it would be drawn once at the full authored alpha
     * and only join the gradient on the next frame, which is a bright speck at
     * the head of an otherwise faded wake. Omitted by the aura layers, which
     * ride the hull and have no band to fade over.
     */
    fade: { hullX: number; hullY: number; limit: number } | null = null,
  ): void {
    const particle = emitter.emitParticleAt(x, y, 1) as unknown as
      | {
          x: number;
          y: number;
          alpha: number;
          __kenoTrail?: TrailParticleBirth | null;
          __kenoFade?: TrailFadeState | null;
        }
      | undefined;
    if (!particle) {
      // At its ceiling: nothing was fired, and there is nothing to place.
      return;
    }
    particle.x = x;
    particle.y = y;
    /*
     * A new life carries nothing of the old one. The pool hands back the same
     * object, and a particle that faded out near the band last time would be
     * born dark if this were left behind.
     */
    particle.__kenoFade = null;
    if (fade !== null) {
      const born = trailFade({
        distance: Math.hypot(x - fade.hullX, y - fade.hullY),
        limit: fade.limit,
        zone: fade.limit,
        currentAlpha: particle.alpha,
        previous: null,
      });
      particle.alpha = born.applied;
      particle.__kenoFade = born;
    }
    if (DEV_TOOLS_ALLOWED && this.pendingBirth) {
      particle.__kenoTrail = this.pendingBirth;
    }
  }
  /**
   * Kills any residue of THIS renderer that has left the band its skin declares.
   *
   * ## Why a distance and not a lifespan
   *
   * See `skin-trail-range.ts` for the algebra and `SPATIAL-CONTRACT.md` for the
   * contract: a lifespan fixed at birth bounds a distance only while the speed
   * does not rise afterwards, and a ship leaving a standstill breaks that on
   * every departure.
   *
   * ## What it touches
   *
   * Only its own emitter's live particles, only their life and their alpha,
   * and only through fields Phaser owns. `kill()` is what marks a particle for
   * recycling on the emitter's next update; the alpha is what stops it being
   * drawn in the frame BETWEEN this call and that update, because the emitter's
   * own renderer skips a particle whose alpha is not above zero
   * (`ParticleEmitterWebGLRenderer.js`). Without it the particle would be drawn
   * once more, one frame of travel past the line.
   *
   * The list is walked backwards so a recycling that ever did splice during the
   * visit could not make it skip an entry. Nothing is allocated.
   */
  private enforceTrailRange(layer: LayerRuntime, state: SkinHostState, diameter: number): void {
    if (layer.plan.role !== 'trail' || layer.plan.maxLengthHulls === null) {
      return;
    }
    const emitter = layer.emitter as unknown as {
      x?: number;
      y?: number;
      alive?: {
        x: number;
        y: number;
        alpha: number;
        kill(): void;
        __kenoFade?: TrailFadeState | null;
      }[];
    } | null;
    const alive = emitter?.alive;
    if (!alive) {
      return;
    }
    /*
     * The emitter's own position is added back for the same reason the census
     * adds it: a particle stores where it was fired in the emitter's space, and
     * that space is the world only while the emitter sits at the origin.
     */
    const baseX = emitter?.x ?? 0;
    const baseY = emitter?.y ?? 0;
    /*
     * The band IS the fade. Residue is a linear gradient: strongest where it
     * leaves the ship, exactly zero at the length the skin declares.
     *
     * A narrower zone was tried first - half a drawn particle, some 8% of the
     * band - and `STEP-CEILING.md` measured why it could not work. A particle's
     * distance advances by up to `step` between two renders, so the brightness
     * it is cut at is bounded by `base * step / zone` and by nothing else. In a
     * duel `step` reaches a whole hull diameter at ordinary frame rates, so a
     * zone of 0.13 bounded nothing: particles were still going out at 0.759 of
     * an authored 0.780.
     *
     * `zone = limit` is the widest a spatial fade can be while staying inside
     * the contract, and it is authored rather than derived: the band was
     * already the rule, and now it is the rule for the brightness too.
     */
    const limit = layer.plan.maxLengthHulls * diameter;
    const zone = limit;
    for (let index = alive.length - 1; index >= 0; index -= 1) {
      const particle = alive[index];
      if (!particle) {
        continue;
      }
      const particleX = baseX + particle.x;
      const particleY = baseY + particle.y;
      if (
        trailParticleOutOfRange({
          hullX: state.x,
          hullY: state.y,
          particleX,
          particleY,
          diameter,
          maxLengthHulls: layer.plan.maxLengthHulls,
        })
      ) {
        particle.kill();
        particle.alpha = 0;
        continue;
      }
      /*
       * Still inside: dim it by how much of the zone is left. The state it
       * carries is what keeps this from compounding frame after frame and from
       * brightening again when a hull turns around - see `trailFade`.
       */
      const faded = trailFade({
        distance: Math.hypot(particleX - state.x, particleY - state.y),
        limit,
        zone,
        currentAlpha: particle.alpha,
        previous: particle.__kenoFade ?? null,
      });
      particle.alpha = faded.applied;
      particle.__kenoFade = faded;
    }
  }
  /**
   * The trail's declared length in world units, or null if this skin has none.
   *
   * One place, because it is now three things at once: where the band cuts,
   * how far the gradient runs, and the displacement above which a frame stops
   * being continuous.
   */
  private trailBandWorldUnits(diameter: number): number | null {
    for (const layer of this.layers) {
      if (layer.plan.role === 'trail' && layer.plan.maxLengthHulls !== null) {
        return layer.plan.maxLengthHulls * diameter;
      }
    }
    return null;
  }

  /** The longest side of a loaded texture, in source pixels. */
  private longestSideOf(key: string): number {
    const source = this.scene.textures.get(key).getSourceImage() as {
      width?: number;
      height?: number;
    };
    return Math.max(1, source.width ?? 16, source.height ?? 16);
  }

  /**
   * Places the hull and advances every layer.
   *
   * The only method the host calls per frame, and it allocates nothing: two
   * small objects from the geometry helpers and no arrays, closures or configs.
   */
  update(state: SkinHostState, deltaMs: number): void {
    if (this.destroyed) {
      return;
    }
    const texture = this.scene.textures.get(this.textureKey);
    const source = texture.getSourceImage();
    const placement = skinSpritePlacement({
      asset: this.assetShape(),
      textureWidth: (source as { width?: number }).width ?? 1,
      textureHeight: (source as { height?: number }).height ?? 1,
      x: state.x,
      y: state.y,
      rotation: state.rotation,
      hullWorldUnits: state.hullWorldUnits,
      visualScale: state.visualScale,
    });
    this.placement = placement;
    this.textureSize = {
      width: (source as { width?: number }).width ?? 1,
      height: (source as { height?: number }).height ?? 1,
    };
    this.hull.setPosition(placement.x, placement.y);
    this.hull.setRotation(placement.rotation);
    this.hull.setScale(placement.scale);

    const diameter = skinHullDiameter(this.assetShape(), state.hullWorldUnits, state.visualScale);

    /*
     * A frame that skipped the whole band is a discontinuity, whoever caused it.
     *
     * Not a teleport invented here, and nothing to do with gameplay: it is the
     * admission that no intermediate sample exists. A wake is drawn by laying
     * residue down along a path, and if the hull covered its own entire declared
     * length between two renders there is no path to lay it along - only two
     * places, and a straight line nobody saw the ship fly.
     *
     * The threshold is the band itself, so it is per skin and needs no new
     * field: 1.6 hull diameters for Silver Hunter, 2.0 for the other two. Below
     * it the gradient does its work, because a particle that moves less than the
     * band has to be drawn at least once with the fade already on it. At or
     * above it, no fade could help - the particle would be drawn at full
     * brightness and gone by the next frame - so the honest answer is to drop
     * the residue rather than to dim it.
     *
     * `clearResidue` also drops `lastDrawn`, and the trail branch refuses to
     * emit without one. So nothing is laid down across the jump either, which
     * is the second half of the rule and costs no extra flag.
     */
    if (
      this.lastDrawn &&
      trailFrameIsDiscontinuous({
        travelledWorldUnits: Math.hypot(state.x - this.lastDrawn.x, state.y - this.lastDrawn.y),
        bandWorldUnits: this.trailBandWorldUnits(diameter),
      })
    ) {
      this.clearResidue();
    }

    this.trailEmitted = false;
    for (const layer of this.layers) {
      this.updateLayer(layer, state, diameter, deltaMs);
    }
    this.lastDrawn = { x: state.x, y: state.y };
  }

  private updateLayer(
    layer: LayerRuntime,
    state: SkinHostState,
    diameter: number,
    deltaMs: number,
  ): void {
    if (layer.plan.circle) {
      /*
       * The seal rides the hull: position, rotation and scale are the ship's,
       * so it reads as something glowing on the plating rather than a decal
       * hovering over the whole object.
       */
      layer.rotation += (layer.plan.circle.rotationsPerSecond * deltaMs) / 1000;
      const size = layer.plan.circle.diameter * diameter;
      for (const [index, seal] of layer.seals.entries()) {
        // Two circles are offset along the hull's own axis, never mirrored
        // outwards, so the pair still reads as being ON the body.
        const spacing = index === 0 ? 0 : -0.12;
        const at = skinHullOffsetToWorld(
          { forward: layer.plan.offset.forward + spacing, lateral: layer.plan.offset.lateral },
          diameter,
          state.rotation,
        );
        seal.setPosition(state.x + at.x, state.y + at.y);
        seal.setDisplaySize(size, size);
        seal.setRotation(state.rotation + layer.rotation * Math.PI * 2);
      }
      return;
    }

    const emitter = layer.emitter;
    if (!emitter) {
      return;
    }

    /*
     * The band is enforced HERE, before anything can return early.
     *
     * Every one of the three early exits below is a frame in which the ship is
     * not laying residue down - too slow to emit, no previous position, or it
     * did not move at all - and residue already in the air does not stop being
     * too far away because the ship stopped feeding it. Gate V26.3 measured
     * exactly that: a wake frozen at 387 diameters across four samples with no
     * displacement at all, which a check that only ran while emitting would
     * have left there until it expired.
     */
    this.enforceTrailRange(layer, state, diameter);

    /*
     * Where the residue is born, and how long it may live.
     *
     * For everything except a trail this is hull-local: the offset is rotated by
     * the ship's own heading, because an overlay and a signature ride the body.
     *
     * A TRAIL is anchored on the ship's REAL movement instead. Its offset is
     * rotated by the direction of travel, so residue is always laid down behind
     * the path the ship actually flew and never into the half-plane it is flying
     * towards. Using the heading here was the bug that made the effect read
     * wrongly the moment the two disagreed - and in the hunt they disagree
     * almost always, because the nose tracks a target while the hull drifts
     * across the arena. Direction of travel is also what the descriptor's own
     * contract says a motion trail follows.
     */
    let anchorRotation = state.rotation;
    let lifespanCapMs = Number.POSITIVE_INFINITY;
    if (layer.plan.role === 'trail') {
      const minimum = (layer.plan.minSpeedHullsPerSecond ?? 0) * diameter;
      if (state.speed < minimum) {
        layer.accumulatorMs = 0;
        return;
      }
      const previous = this.lastDrawn;
      if (!previous) {
        // No previous frame means no known direction of travel, and a trail
        // whose direction is a guess would point somewhere the ship never went.
        layer.accumulatorMs = 0;
        return;
      }
      const dx = state.x - previous.x;
      const dy = state.y - previous.y;
      if (dx === 0 && dy === 0) {
        layer.accumulatorMs = 0;
        return;
      }
      const travel = Math.atan2(dy, dx);
      this.trailEmitted = true;
      // The offset helper reads a SPRITE rotation, where forward is the art's
      // -Y. A quarter turn converts the direction of travel into that frame.
      anchorRotation = travel + Math.PI / 2;
      emitter.particleRotate = Phaser.Math.RadToDeg(travel);
      /*
       * A cheaper first pass at the band, and NOT the guarantee of it.
       *
       * Residue is static in the world, so the band a trail draws is how far
       * the ship travels during one particle's life - and capping the lifespan
       * by `length / speed` bounds that only while the speed does not rise
       * afterwards. This comment used to claim it was "the only form that holds
       * at every speed". It is not, and the measurement is unambiguous: of 424
       * particles found outside their contract, 402 had been shortened by this
       * very cap and broke the length anyway. One was followed from birth - laid
       * down at 85 world units per second, capped at 979 ms for that speed, and
       * still alive 6.7 diameters behind a hull whose contract is 1.6 once the
       * ship had accelerated to 340-491.
       *
       * What it still earns is work: at a steady speed it keeps the pool small
       * rather than leaving `enforceTrailRange` to kill a long tail every frame.
       * The band itself is guaranteed there, in world units, per particle.
       */
      const maxLength = layer.plan.maxLengthHulls;
      if (maxLength !== null && state.speed > 0) {
        lifespanCapMs = (maxLength * diameter * 1000) / state.speed;
      }
    }

    const anchor = skinHullOffsetToWorld(layer.plan.offset, diameter, anchorRotation);
    const originX = state.x + anchor.x;
    const originY = state.y + anchor.y;

    /*
     * The palette advances on a CLOCK, not once per particle.
     *
     * Taking the next colour per emission made the palette a per-particle
     * dither: at any real cadence the whole cycle fits inside a few pixels of
     * trail, so four saturated stops summed to white instead of reading as four
     * colours. Held for a slice of the layer's own longest lifespan, the same
     * palette becomes a sweep the eye can follow - the newest residue is one
     * colour, the oldest is the one before it, and the band shifts hue as the
     * ship flies. Coherent in space and in time, and derived: a skin with a
     * long-lived trail sweeps slowly, a short-lived one sweeps fast, and
     * nothing here knows which skin it is.
     */
    const holdMs = Math.max(1, layer.plan.lifespanMs.max / Math.max(1, layer.plan.colors.length));
    layer.paletteHoldMs += deltaMs;
    const held = Math.floor(layer.paletteHoldMs / holdMs);
    if (held > 0) {
      layer.paletteHoldMs -= held * holdMs;
      layer.colorIndex += held;
    }

    /*
     * A rate in hertz, honoured with an accumulator rather than "one per
     * frame". Emitting per frame would make the effect three times denser on a
     * 180 Hz panel than on a 60 Hz one, which is a descriptor meaning different
     * things on different machines.
     */
    layer.accumulatorMs += deltaMs;
    const interval = 1000 / layer.plan.frequencyHz;
    // Bounded: a tab that was hidden for a minute must not emit a minute's
    // worth of particles the instant it comes back.
    let emissions = 0;
    while (layer.accumulatorMs >= interval && emissions < 4) {
      layer.accumulatorMs -= interval;
      emissions += 1;
      const colors = layer.plan.colors;
      const color = colors[layer.colorIndex % colors.length] as string;
      emitter.particleTint = skinColorValue(color);
      const scale =
        layer.plan.scale.min + Math.random() * (layer.plan.scale.max - layer.plan.scale.min);
      // A fraction of the HULL, whatever shape draws it: the longest side of the
      // texture is normalised away, so the aspect ratio the art has is kept.
      const factor = (scale * diameter) / layer.textureLongestSide;
      emitter.particleScaleX = factor;
      emitter.particleScaleY = factor;
      const life =
        layer.plan.lifespanMs.min +
        Math.random() * (layer.plan.lifespanMs.max - layer.plan.lifespanMs.min);
      emitter.setParticleLifespan(Math.max(1, Math.min(life, lifespanCapMs)));
      /*
       * Where the particle is born. Local decorative variation only: it moves a
       * spark by a fraction of a hull and can never reach a signal, because the
       * spread the descriptor declares is bounded at half a hull by the schema.
       *
       * A TRAIL scatters differently, and the reason is a fact about this game
       * rather than a preference. The hunt's ship drifts at about 0.21 hulls per
       * second, so in one particle's life it travels a THIRD of a hull - and
       * residue laid down isotropically therefore piles into one clump sitting a
       * hull behind the ship, detached from it, reading as a smudge somebody
       * left rather than as a wake. Elongating the birth scatter along the
       * travel axis by the emission offset spreads that same residue from just
       * behind the hull outwards, which is the shape a wake has. Lateral grain
       * stays at the declared spread, so the plume has width without becoming a
       * line.
       */
      const spreadUnits = layer.plan.spread * diameter;
      if (layer.plan.role === 'trail') {
        const along = (Math.random() - 0.5) * Math.abs(layer.plan.offset.forward) * diameter;
        const lateral = (Math.random() - 0.5) * spreadUnits;
        // `anchorRotation` is a sprite rotation, whose forward is -Y.
        const cos = Math.cos(anchorRotation);
        const sin = Math.sin(anchorRotation);
        const bornX = originX + lateral * cos + along * sin;
        const bornY = originY + lateral * sin - along * cos;
        if (DEV_TOOLS_ALLOWED) {
          this.birthSeq += 1;
          this.pendingBirth = {
            seq: this.birthSeq,
            atMs: this.scene.time.now,
            hullX: state.x,
            hullY: state.y,
            bornX,
            bornY,
            diameter,
            maxLengthHulls: layer.plan.maxLengthHulls,
            speed: state.speed,
            minSpeed: (layer.plan.minSpeedHullsPerSecond ?? 0) * diameter,
            capMs: lifespanCapMs,
            authoredMs: life,
            appliedMs: Math.max(1, Math.min(life, lifespanCapMs)),
            travelRad: anchorRotation - Math.PI / 2,
          };
        }
        this.emitAt(
          emitter,
          bornX,
          bornY,
          layer.plan.maxLengthHulls === null
            ? null
            : {
                hullX: state.x,
                hullY: state.y,
                limit: layer.plan.maxLengthHulls * diameter,
              },
        );
      } else {
        /*
         * The aura layers are emitted through the same door, and for the same
         * reason: they place absolute world coordinates too, so a recycled
         * particle of theirs would land on old + new exactly as a trail one
         * does. Fixing only the wake would have left the overlay and the
         * signatures drifting for the rest of a match.
         */
        /*
         * An OVERLAY is body sparkle, and a body is not a rectangle.
         *
         * It used to be born in a disc of `spreadUnits` around a hull-local
         * offset. A ship's PNG is 45% to 55% transparent, so a good share of
         * every skin's sparkle was appearing in the void beside the hull - the
         * player's words were "sem energia aderida ao corpo opaco da nave".
         *
         * The points come from the sidecar, where the pipeline read them out of
         * this skin's own alpha with an erosion applied, and they are placed by
         * the SAME transform the hull sprite got. No texture is sampled here
         * and no array is built: one index, one lookup, one pair of numbers.
         */
        const maskPoints = this.plan.bodyPoints;
        const placed = this.placement;
        if (layer.plan.role === 'overlay' && maskPoints.length > 0 && placed !== null) {
          const point = maskPoints[this.maskSeq % maskPoints.length];
          this.maskSeq += 1;
          if (point) {
            const at = skinTexturePointToWorld({
              point,
              placement: placed,
              textureWidth: this.textureSize.width,
              textureHeight: this.textureSize.height,
            });
            this.emitAt(emitter, at.x, at.y);
            continue;
          }
        }
        this.emitAt(
          emitter,
          originX + (Math.random() - 0.5) * spreadUnits,
          originY + (Math.random() - 0.5) * spreadUnits,
        );
      }
    }
    if (layer.accumulatorMs > interval * 4) {
      layer.accumulatorMs = 0;
    }
  }

  /**
   * Drops every particle in the air, for a discontinuity.
   *
   * A teleport, an authoritative correction, a world replacement and a skin
   * change are all the same thing: the ship was there and is now here, and it
   * did not travel between them. A trail that survived one would draw a line
   * across the arena that no ship ever flew.
   *
   * The EMITTERS survive - a correction is not the end of a duel - and only
   * {@link SkinRenderer.destroy} takes those.
   */
  clearResidue(): void {
    for (const layer of this.layers) {
      layer.emitter?.killAll();
      layer.accumulatorMs = 0;
      layer.paletteHoldMs = 0;
    }
    this.lastDrawn = null;
  }

  /** Everything this renderer made, released. Idempotent. */
  destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    for (const layer of this.layers) {
      layer.emitter?.killAll();
      layer.emitter?.destroy();
      for (const seal of layer.seals) {
        seal.destroy();
      }
      layer.seals.length = 0;
    }
    this.layers.length = 0;
    this.lastDrawn = null;
  }

  /** What the plan says about the art, in the shape the geometry helpers take. */
  private assetShape() {
    return {
      pivot: this.plan.pivot,
      hullCenter: this.plan.hullCenter,
      baseScale: this.plan.baseScale,
      // Not used by the two helpers this class calls; present so the shape
      // satisfies the metadata type without the plan having to carry prose.
      visualBounds: { left: 0, top: 0, right: 1, bottom: 1 },
    } as never;
  }

  /** How many particles are alive across every layer. For the census. */
  aliveParticles(): number {
    let total = 0;
    for (const layer of this.layers) {
      total += layer.emitter?.getAliveParticleCount() ?? 0;
    }
    return total;
  }

  /**
   * Whether residue is being laid down, and in which direction the ship is
   * travelling while it is. For the census.
   */
  motion(): { trailEmitting: boolean; trailParticles: number } {
    let trailParticles = 0;
    for (const layer of this.layers) {
      if (layer.plan.role === 'trail') {
        trailParticles += layer.emitter?.getAliveParticleCount() ?? 0;
      }
    }
    return { trailEmitting: this.trailEmitted, trailParticles };
  }

  /** How many emitters this renderer owns. For the census. */
  emitterCount(): number {
    return this.layers.filter((layer) => layer.emitter !== null).length;
  }
}
